import { create } from 'zustand'

type ToastTone = 'info' | 'error'

export type ToastSpec = {
  id: number
  message: string
  tone: ToastTone
  duration?: number
}

type ViewState = {
  center: { x: number; y: number }
  zoom: number
}

type StoreState = {
  selectedId: string | null
  timeMs: number
  playing: boolean
  lastUpdated: {
    top10?: number
    objects: Record<string, number>
  }
  viewState: ViewState
  toasts: ToastSpec[]
}

type StoreActions = {
  setSelectedId: (id: string | null, opts?: { retainViewState?: boolean }) => void
  setTimeMs: (ms: number | ((prev: number) => number)) => void
  setPlaying: (playing: boolean) => void
  setTopUpdated: (ms: number) => void
  setObjectUpdated: (id: string, ms: number) => void
  setViewState: (view: Partial<ViewState>) => void
  pushToast: (payload: { message: string; tone?: ToastTone; duration?: number }) => number
  dismissToast: (id: number) => void
}

type Store = StoreState & StoreActions

const createDefaultViewState = (): ViewState => ({
  center: { x: 0, y: 0 },
  zoom: 1,
})

let toastIdCounter = 0
const toastTimers = new Map<number, ReturnType<typeof setTimeout>>()

export const useStore = create<Store>((set, get) => ({
  selectedId: null,
  timeMs: Date.now(),
  playing: true,
  lastUpdated: { objects: {} },
  viewState: createDefaultViewState(),
  toasts: [],
  setSelectedId: (id, opts) => {
    set((state) => {
      if (state.selectedId === id) return state
      const shouldResetView = !opts?.retainViewState
      return {
        selectedId: id,
        viewState: shouldResetView ? createDefaultViewState() : state.viewState,
      }
    })
  },
  setTimeMs: (ms) =>
    set((state) => ({
      timeMs: typeof ms === 'function' ? ms(state.timeMs) : ms,
    })),
  setPlaying: (playing) => set({ playing }),
  setTopUpdated: (ms) =>
    set((state) => ({
      lastUpdated: { ...state.lastUpdated, top10: ms },
    })),
  setObjectUpdated: (id, ms) =>
    set((state) => ({
      lastUpdated: {
        ...state.lastUpdated,
        objects: { ...state.lastUpdated.objects, [id]: ms },
      },
    })),
  setViewState: (view) =>
    set((state) => ({
      viewState: {
        center: view.center
          ? { x: view.center.x, y: view.center.y }
          : state.viewState.center,
        zoom: view.zoom ?? state.viewState.zoom,
      },
    })),
  pushToast: ({ message, tone = 'info', duration = 6000 }) => {
    const id = ++toastIdCounter
    set((state) => ({
      toasts: [...state.toasts, { id, message, tone, duration }],
    }))

    if (duration > 0) {
      const timer = setTimeout(() => {
        get().dismissToast(id)
      }, duration)
      toastTimers.set(id, timer)
    }

    return id
  },
  dismissToast: (id) => {
    const timer = toastTimers.get(id)
    if (timer) {
      clearTimeout(timer)
      toastTimers.delete(id)
    }
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }))
  },
}))

export function getViewDefaults() {
  return createDefaultViewState()
}
